import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Search, Loader2, BookOpen, Calendar, Tag, ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const [searchInput, setSearchInput] = useState(q);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sp = new URLSearchParams();
      if (q) sp.append("q", q);
      if (category) sp.append("category", category);
      sp.append("limit", "50");
      const [arts, cats] = await Promise.all([
        api.get(`/articles?${sp.toString()}`),
        api.get("/articles/categories"),
      ]);
      setArticles(Array.isArray(arts.data) ? arts.data : []);
      setCategories(Array.isArray(cats.data) ? cats.data : []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [q, category]);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (searchInput.trim()) next.set("q", searchInput.trim()); else next.delete("q");
    setParams(next);
  };

  const setCategory = (cat) => {
    const next = new URLSearchParams(params);
    if (cat) next.set("category", cat); else next.delete("category");
    setParams(next);
  };

  return (
    <div>
      <SEO
        title="Blog y artículos de salud mental"
        description="Artículos educativos sobre psiquiatría, neurología y salud mental escritos por el Dr. César S. Martínez Maldonado. Información confiable para pacientes y familias."
        canonical="/blog"
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-cyan-900 to-teal-900 py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur text-white text-sm font-medium border border-white/20 mb-5">
            <BookOpen size={16} /> Blog de salud mental
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Recursos educativos de salud mental
          </h1>
          <p className="text-lg md:text-xl text-cyan-100 max-w-2xl mx-auto mb-8">
            Artículos especializados sobre psiquiatría, neurología y bienestar emocional escritos por el Dr. César Martínez.
          </p>
          <form onSubmit={onSearch} className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar artículos..."
              className="w-full pl-12 pr-28 py-4 rounded-full bg-white text-slate-900 focus:outline-none focus:ring-4 focus:ring-cyan-400/50 shadow-xl"
            />
            <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-600 to-teal-600 text-white font-semibold text-sm hover:shadow-lg transition">
              Buscar
            </button>
          </form>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 py-12 md:py-16">
        {/* Categories filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            <button
              onClick={() => setCategory("")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${!category ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-cyan-400"}`}
            >
              Todas
            </button>
            {categories.map((c) => (
              <button
                key={c.name}
                onClick={() => setCategory(c.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${category === c.name ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-cyan-400"}`}
              >
                {c.name} <span className="opacity-60">({c.count})</span>
              </button>
            ))}
          </div>
        )}

        {/* Active filters */}
        {(q || category) && (
          <div className="mb-6 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-500">Filtros:</span>
            {q && (
              <span className="inline-flex items-center gap-1.5 bg-cyan-50 text-cyan-800 px-3 py-1 rounded-full text-sm">
                "{q}"
                <button onClick={() => { setSearchInput(""); const n = new URLSearchParams(params); n.delete("q"); setParams(n); }} className="hover:text-rose-600">×</button>
              </span>
            )}
            {category && (
              <span className="inline-flex items-center gap-1.5 bg-cyan-50 text-cyan-800 px-3 py-1 rounded-full text-sm">
                {category}
                <button onClick={() => setCategory("")} className="hover:text-rose-600">×</button>
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-cyan-600" size={32} /></div>
        ) : articles.length === 0 ? (
          <div className="py-20 text-center">
            <BookOpen className="mx-auto mb-4 text-slate-300" size={48} />
            <h3 className="text-xl font-bold text-slate-900 mb-2">No se encontraron artículos</h3>
            <p className="text-slate-500">Prueba con otros términos o filtros</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((a) => (
              <Link
                key={a.id}
                to={`/articulos/${a.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-slate-100"
              >
                {a.featured_image && (
                  <div className="relative h-52 overflow-hidden">
                    <img src={a.featured_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-semibold text-cyan-700">
                      {a.category}
                    </span>
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-bold text-slate-900 text-lg leading-tight mb-2 line-clamp-2 group-hover:text-cyan-700 transition">
                    {a.title}
                  </h3>
                  <p className="text-slate-600 text-sm line-clamp-3 mb-3">{a.excerpt}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar size={12} />
                    {a.published_at && new Date(a.published_at).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                  {Array.isArray(a.tags) && a.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-slate-100">
                      {a.tags.slice(0, 3).map((t) => (
                        <span key={t} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                          <Tag size={9} className="inline mr-0.5" />{t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
